import React from "react";
import { LockKeyhole } from "lucide-react";

function normalizeCurrencyCode(currencyCode) {
  const code = String(currencyCode || "").trim().toUpperCase();
  return code || "EUR";
}

export default function PreviewModeSeparator({
  show = false,
  colSpan = 8,
  language = "de",
  currencyCode = "EUR",
  variant = "row",
  className = "",
}) {
  if (!show) return null;

  const isEnglish = language === "en";
  const cc = normalizeCurrencyCode(currencyCode);

  const title = isEnglish
    ? `Remaining ${cc} values are locked in Preview Mode`
    : `Weitere ${cc}-Werte sind im Vorschau-Modus gesperrt`;

  const subtitle = isEnglish
    ? "Unlock to view all calculated values, yearly totals and exports."
    : "Freischalten für alle berechneten Werte, Jahres-Summen und Exporte.";

  const Content = (
    <div className="relative isolate overflow-hidden px-6 py-5">
      {/* soft transition field */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_62%_135%_at_50%_50%,rgba(99,102,241,0.12),transparent_67%),linear-gradient(90deg,rgba(255,255,255,0.98),rgba(238,242,255,0.78)_48%,rgba(255,255,255,0.98))]" />

      {/* premium fade lines */}
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-indigo-200/90 to-transparent" />
      <div className="pointer-events-none absolute inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />

      {/* connector line behind chip */}
      <div className="pointer-events-none absolute left-8 right-8 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-indigo-200/90 to-transparent" />

      {/* subtle vertical fade, makes transition feel intentional */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/65 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/65 to-transparent" />

      <div className="relative flex justify-center">
        <div
          className={[
            "group relative inline-flex max-w-[92%] items-center gap-3 overflow-hidden rounded-2xl",
            "border border-indigo-100/90 bg-white/90 px-4 py-2.5",
            "shadow-[0_12px_34px_rgba(30,58,138,0.085),inset_0_1px_0_rgba(255,255,255,0.96)]",
            "ring-1 ring-white/90 backdrop-blur-md",
            "transition-all duration-300 ease-out",
            "hover:-translate-y-px hover:border-indigo-200/90 hover:bg-white",
            "hover:shadow-[0_16px_42px_rgba(30,58,138,0.13),inset_0_1px_0_rgba(255,255,255,0.98)]",
          ].join(" ")}
        >
          {/* chip background */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.94))]" />

          {/* hover shine */}
          <div className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/70 to-transparent opacity-0 blur-sm transition-all duration-700 ease-out group-hover:left-full group-hover:opacity-100" />

          {/* top highlight */}
          <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />

          <div
            className={[
              "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
              "border border-indigo-100 bg-gradient-to-br from-white via-indigo-50 to-blue-50 shadow-sm",
              "transition-all duration-300 ease-out",
              "group-hover:border-indigo-200 group-hover:shadow-[0_0_0_4px_rgba(99,102,241,0.08)]",
            ].join(" ")}
          >
            <LockKeyhole className="h-3.5 w-3.5 text-indigo-600 transition-transform duration-300 ease-out group-hover:scale-110" />
          </div>

          <div className="relative min-w-0 pr-1">
            <div className="truncate text-[11px] font-semibold text-slate-700">
              {title}
            </div>
            <div className="mt-0.5 hidden text-[10px] leading-snug text-slate-500 sm:block">
              {subtitle}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (variant === "block") {
    return (
      <div
        className={[
          "preview-mode-lock-separator-block relative my-2 overflow-hidden rounded-2xl border border-indigo-100/70 bg-white",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        role="separator"
      >
        {Content}
      </div>
    );
  }

  return (
    <tr
      className={[
        "preview-mode-lock-separator border-y border-indigo-100/60 bg-white",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      role="separator"
    >
      <td colSpan={colSpan} className="p-0">
        {Content}
      </td>
    </tr>
  );
}
